/**
 * EntityDetailHeader - Header component for entity detail page
 * Displays title, back button, and action buttons (verify, edit, save, delete)
 * @idempotent @atomic
 */
import { memo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  ShieldCheckIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline'

const EntityDetailHeader = memo(function EntityDetailHeader({
  isEditMode,
  editing,
  entity,
  onEdit,
  onSave,
  onCancel,
  onVerify,
  onDelete,
  loading
}) {
  const navigate = useNavigate()

  return (
    <div className="mb-6">
      <button
        onClick={() => navigate('/admin/entities')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5 mr-2" />
        Back to Entities
      </button>
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode ? 'Entity Details' : 'Create New Entity'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEditMode ? 'View and manage entity information' : 'Add a new entity to the system'}
          </p>
        </div>
        
        {isEditMode && entity && (
          <div className="flex gap-2">
            {!editing ? (
              <>
                {!entity.isVerified && (
                  <button
                    onClick={onVerify}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    <ShieldCheckIcon className="h-5 w-5 mr-2" />
                    Verify
                  </button>
                )}
                <button
                  onClick={onEdit}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  <PencilIcon className="h-5 w-5 mr-2" />
                  Edit
                </button>
                <button
                  onClick={onDelete}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  <TrashIcon className="h-5 w-5 mr-2" />
                  Delete
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onSave}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
                >
                  <CheckIcon className="h-5 w-5 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={onCancel}
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 mr-2" />
                  Cancel
                </button>
              </>
            )}
          </div>
        )}
        
        {!isEditMode && (
          <div className="flex gap-2">
            <button
              onClick={onSave}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition-colors"
            >
              <CheckIcon className="h-5 w-5 mr-2" />
              {loading ? 'Creating...' : 'Create Entity'}
            </button>
            <button
              onClick={() => navigate('/admin/entities')}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 mr-2" />
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  )
})

export default EntityDetailHeader
